import type { Judgement } from "./judge.ts";
import { mastered, meetsMastery } from "./progression.ts";
import { transact } from "./store.ts";

/** What has been delivered for one request, for the ledger (keyed by the request's id). */
export interface RequestRecord {
  deliveries: number;
  /** The best delivery by points (by strokes, fewest first, between equal points). */
  bestTotal: number;
  bestRank: string;
  bestStrokes: number;
  /** The customer's wish came true at least once. */
  wishMet: boolean;
  /** Optional challenge stamp, earned by one complete delivery (not necessarily the best). */
  mastered?: boolean;
  /** Delivered at least once with the holes in place while the shape was still far off. */
  topological?: boolean;
  /** True for sunglasses first delivered as the two-hole intro; absent in older saves. */
  sunglassesIntro?: boolean;
}

const STORE = "records";

/** Last readable ledger plus this session's unsaved deliveries (private/quota-limited browsers). */
let cached = new Map<string, RequestRecord>();
const unsaved = new Map<string, RequestRecord>();
export let recordsPersistent = true;

/** Pure reducer: score, stroke count and mastery always come from the same attempt. */
export function nextRecord(
  id: string,
  was: RequestRecord | undefined,
  judgement:
    & Pick<Judgement, "total" | "rank" | "wishMet">
    & Partial<Pick<Judgement, "topological">>,
  strokes: number,
): RequestRecord {
  const best = !was || judgement.total > was.bestTotal ||
    (judgement.total === was.bestTotal && strokes < was.bestStrokes);
  return {
    deliveries: (was?.deliveries ?? 0) + 1,
    ...(id === "boutique-sunglasses"
      ? { sunglassesIntro: was && was.deliveries > 0 ? !!was.sunglassesIntro : true }
      : {}),
    bestTotal: best ? judgement.total : was.bestTotal,
    bestRank: best ? judgement.rank : was.bestRank,
    bestStrokes: best ? strokes : was.bestStrokes,
    wishMet: judgement.wishMet || !!was?.wishMet,
    mastered: mastered(id, was) || meetsMastery(id, judgement.total, strokes),
    ...(judgement.topological || was?.topological ? { topological: true } : {}),
  };
}

/** Notes a delivery; storage failure still permits progression for this tab's session. */
export async function recordDelivery(
  id: string,
  judgement: Judgement,
  strokes: number,
): Promise<boolean> {
  await loadRecords();
  let record = nextRecord(id, cached.get(id), judgement, strokes);
  try {
    await transact(STORE, "readwrite", (s) => {
      const get = s.get(id);
      get.onsuccess = () => {
        record = nextRecord(id, unsaved.get(id) ?? get.result, judgement, strokes);
        s.put(record, id);
      };
    });
    unsaved.delete(id);
    recordsPersistent = unsaved.size === 0;
  } catch {
    // Another failed delivery may have completed while this transaction was pending.
    record = nextRecord(id, unsaved.get(id) ?? cached.get(id), judgement, strokes);
    unsaved.set(id, record);
    recordsPersistent = false;
  }
  cached.set(id, record);
  return recordsPersistent;
}

/** Replaces the session view after an explicit import or reset. */
export function replaceRecordCache(records: ReadonlyMap<string, RequestRecord>): void {
  cached = new Map(records);
  unsaved.clear();
  recordsPersistent = true;
}

/** Blanks the ledger and its progression. The shelf is independent. */
export async function clearRecords(): Promise<void> {
  await transact(STORE, "readwrite", (s) => s.clear());
  replaceRecordCache(new Map());
}

/** Every request's record, by the request's id. */
export async function loadRecords(): Promise<Map<string, RequestRecord>> {
  const records = new Map<string, RequestRecord>();
  try {
    await transact(STORE, "readonly", (s) => {
      const cursor = s.openCursor();
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) return;
        records.set(String(c.key), c.value);
        c.continue();
      };
    });
    for (const [id, record] of unsaved) records.set(id, record);
    cached = records;
    recordsPersistent = unsaved.size === 0;
  } catch {
    recordsPersistent = false;
  }
  return new Map(cached);
}
